import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient.ts'
import {errorHandler} from '@utilities/helpers.ts'
import {captureException} from '@sentry/react'
import {Assignment} from '@features/assignment/types.ts'
import {httpAssignmentCoordination, HttpAssignmentCoordinationOptions} from '@features/assignment/http/assignment.http'
import {parseAxiosPromise} from '@/axiosInstance.ts'

export const useAssignmentCoordination = ({
    assignmentId,
    taskId,
    options
}: {
    assignmentId: number
    taskId: number
    options?: Omit<UseMutationOptions<Assignment, unknown, HttpAssignmentCoordinationOptions['payload']>, 'mutationFn'>
}) => {
    const queryClient = useQueryClient()

    return useMutation({
        ...options,
        mutationFn: (values: HttpAssignmentCoordinationOptions['payload']) =>
            parseAxiosPromise({
                axiosPromise: httpAssignmentCoordination({
                    payload: values,
                    urlParams: {assignmentId, taskId},
                    params: {response_type: 'extended'}
                }),
                responseShape: Assignment,
                onZodError: captureException
            }),
        onError: errorHandler,
        onSuccess: (data, variables, context) => {
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.CURRENT_ASSIGNMENTS]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACTIVE_ASSIGNMENTS_RANGE]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACTIVE_ASSIGNMENTS_TABLE]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ASSIGNMENTS_COUNT]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ASSIGNMENTS]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.HOME_TABLE]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.REQUIRED_ACTIONS]})
            void queryClient.setQueriesData({queryKey: [QUERY_KEYS.ASSIGNMENT, data.code]}, data)
            options?.onSuccess?.(data, variables, context)
        }
    })
}
